/* eslint-disable ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/require-computed-property-dependencies, no-restricted-properties, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import StaffPageAccess from '@linc-technologies/linced-common/enums/roles/staff-page-access';
import FieldAccess from '@linc-technologies/linced-common/enums/roles/field-access';
import Staff from '@linc-technologies/linced-common/enums/roles/staff';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { tagName } from '@ember-decorators/component';
import { rejectBy } from '@linc-technologies/linced-common/utils/arrays';

@tagName('')
export default class StaffRolesManager extends Component {
  @service
  features;

  @service
  options;

  @service
  permissions;

  @service
  roles;

  @service
  store;

  @service
  toaster;

  // person specifies the Person whose roles are being managed.  Required.
  person = null;

  // The form to register the rendered fields with, so they count towards the
  // form's validity.
  parentComponent = null;

  PersonType = PersonType;
  StaffPageAccess = StaffPageAccess;
  FieldAccess = FieldAccess;
  Staff = Staff;
  Scopes = Scopes;

  nonSensitiveAccessLevels = Object.freeze([
    { value: 'learnerpages:restricted', label: 'Restricted' },
    { value: 'learnerpages:standard', label: 'Standard' },
  ]);

  nonSensitiveAdditionalPerms = Object.freeze([
    {
      value: 'staff:processEnrolmentApplications',
      label: 'Process Enrolment Applications',
    },
    { value: 'staff:pinPosts', label: 'Can Pin Posts' },
    { value: 'staff:approveEvents', label: 'Can Approve Events' },
  ]);

  @computed('isPrincipalOrDelegate', 'isLincStaff')
  get visibleLevels() {
    if (this.isPrincipalOrDelegate || this.isLincStaff) {
      return StaffPageAccess.values;
    }
    return this.nonSensitiveAccessLevels;
  }

  @computed('isPrincipalOrDelegate', 'isLincStaff', 'features.eventsEnabled')
  get visiblePerms() {
    let visiblePerms = this.nonSensitiveAdditionalPerms;
    if (this.isPrincipalOrDelegate || this.isLincStaff) {
      visiblePerms = Staff.values;
    }

    if (!this.features.eventsEnabled) {
      visiblePerms = rejectBy(visiblePerms, 'value', 'staff:approveEvents');
    }

    return visiblePerms;
  }

  @computed('person.userId')
  get user() {
    if (isEmpty(this.person.userId)) {
      return null;
    }
    return this.store.peekRecord('user', this.person.userId);
  }

  @computed('person.type', 'user.scopes')
  get isTenantAdmin() {
    if (this.person.type !== PersonType.STAFF) {
      // Only staff can be tenant admins.
      return false;
    }
    if (isEmpty(this.user) || isEmpty(this.user.scopes)) {
      // Can't be a tenant admin if you don't have a user account (or scopes).
      return false;
    }
    return this.user.scopes.includes(Scopes.TENANT_ADMIN);
  }

  @alias('person.pageAccessLevel')
  pageAccessLevel;

  @alias('roles.isPrincipalOrDelegate')
  isPrincipalOrDelegate;

  @alias('roles.isLincStaff')
  isLincStaff;

  @action
  setPageAccessLevel(pageAccessLevel) {
    this.person.setPageAccessLevel(pageAccessLevel);
  }

  @action
  toggleRole(role, checked) {
    if (checked) {
      this.person.roles.pushObject(role);
    } else {
      this.person.roles.removeObject(role);
    }
  }
}
